import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownToLine,
  ArrowRight,
  ArrowUpFromLine,
  BarChart3,
  CalendarDays,
  LineChart,
  Plus,
  Sparkles,
  Target,
  Trophy,
} from "lucide-react";
import {
  dashboardApi,
  reportsApi,
  walletApi,
  type CalendarDay,
  type DashboardSummary,
  type DayBreakdown,
} from "../lib/api";
import { formatCurrency, formatDate, formatSignedCurrency } from "../lib/format";
import { useAuth } from "../lib/auth";
import { useElementWidth } from "../lib/useElementWidth";
import { useI18n } from "../lib/i18n";
import CashflowChart from "../components/CashflowChart";

const TIER_STYLES: Record<string, string> = {
  Bronze: "tier-bronze",
  Silver: "tier-silver",
  Gold: "tier-gold",
  Platinum: "tier-platinum",
  Diamond: "tier-diamond",
};

/**
 * One day of the current week. A pared-back sibling of the calendar's DayCell:
 * same colour rule (only TRADING result tints a day -- a deposit must not read
 * as a winning session) and the same savings-is-not-a-loss gold, but no trade
 * counts or icons, because seven of these share one dashboard row.
 */
function WeekDayCell({ day }: { day: CalendarDay }) {
  const hasTrades = day.trades > 0;
  const isToday = day.date === new Date().toISOString().slice(0, 10);
  const tone = !hasTrades
    ? "border-glass"
    : day.net_pl > 0
      ? "border-emerald-500/30 bg-emerald-500/[0.07]"
      : day.net_pl < 0
        ? "border-rose-500/30 bg-rose-500/[0.07]"
        : "border-glass";
  const plCls =
    day.net_pl > 0 ? "text-pnl-pos" : day.net_pl < 0 ? "text-pnl-neg" : "text-parchment-muted";

  return (
    <div
      className={`rounded-lg border p-1.5 sm:p-2 min-h-[4rem] flex flex-col gap-0.5 ${tone} ${
        isToday ? "ring-1 ring-accent" : ""
      }`}
    >
      <span
        className={`text-[10px] uppercase tracking-wide ${
          isToday ? "text-accent font-bold" : "text-parchment-muted"
        }`}
      >
        {new Date(`${day.date}T00:00:00`).toLocaleDateString(undefined, { weekday: "short" })}
      </span>
      {hasTrades && (
        <span className={`text-[11px] sm:text-xs font-semibold tabular leading-tight ${plCls}`}>
          {formatSignedCurrency(day.net_pl)}
        </span>
      )}
      <span className="mt-auto flex flex-wrap gap-x-1 leading-none">
        {day.deposits > 0 && (
          <span className="text-[9px] text-pnl-pos tabular">+{formatCurrency(day.deposits)}</span>
        )}
        {day.withdrawals > 0 && (
          <span className="text-[9px] text-pnl-neg tabular">-{formatCurrency(day.withdrawals)}</span>
        )}
        {day.savings > 0 && (
          <span className="text-[9px] text-accent tabular">{formatCurrency(day.savings)}</span>
        )}
      </span>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const q = useQuery({ queryKey: ["dashboard-summary"], queryFn: () => dashboardApi.summary() });

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-parchment-muted text-xs uppercase tracking-widest">{t("nav.dashboard")}</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-parchment mt-1">
            {t("dash.welcome")} <span className="text-accent">{user?.name || t("dash.trader")}</span>
          </h1>
        </div>
      </header>

      {q.isLoading && <p className="text-parchment-muted text-sm">{t("common.loading")}</p>}
      {q.isError && (
        <p className="rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-sm text-pnl-neg">
          {(q.error as Error)?.message ?? t("dash.loadError")}
        </p>
      )}

      {q.data && <Body summary={q.data} />}
    </>
  );
}

function Body({ summary }: { summary: DashboardSummary }) {
  const { t } = useI18n();
  const { progress, stats, balance, saved_total, account_balance, has_target, activity, week } = summary;
  const analyticsQ = useQuery({ queryKey: ["wallet-analytics"], queryFn: () => walletApi.analytics() });
  const reportsQ = useQuery({ queryKey: ["reports-overview"], queryFn: () => reportsApi.overview() });
  const tradesSub = (n: number) =>
    n === 1 ? t("dash.tradeOne", { count: n }) : t("dash.tradeMany", { count: n });

  return (
    <>
      {!has_target && <SetupBanner />}

      {/* Hero: tier + level + progress */}
      <section className="glass-gold p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-glow pointer-events-none" />
        <div className="relative grid gap-6 lg:grid-cols-[1.2fr_1.8fr] items-center">
          <div>
            <p className="text-parchment-muted text-xs uppercase tracking-widest">{t("dash.currentTier")}</p>
            <div className="mt-1 flex items-center gap-3">
              <span
                className={
                  "inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-bold uppercase tracking-widest " +
                  (TIER_STYLES[progress.tier] ?? "text-parchment border-glass-mid bg-surface-2")
                }
              >
                <Trophy className="w-4 h-4" />
                {progress.tier}
              </span>
              <span className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gold-linear">
                L{progress.level}
              </span>
            </div>
            <p className="mt-3 text-sm text-parchment-muted">
              {t("dash.balance")}{" "}
              <span className="tabular text-parchment font-semibold">{formatCurrency(balance)}</span>
            </p>
            {/* Money banked with a savings withdrawal has left the account but
                still counts toward the level, so show where it actually sits.
                Nothing saved means the two figures are equal -- no split. */}
            {saved_total > 0 && (
              <p className="mt-1 text-xs text-parchment-muted flex flex-wrap gap-x-3 gap-y-0.5">
                <span className="tabular">
                  {t("wallet.inAccount", { amount: formatCurrency(account_balance) })}
                </span>
                <span className="tabular text-accent">
                  {t("wallet.savedTotal", { amount: formatCurrency(saved_total) })}
                </span>
              </p>
            )}
          </div>

          <div>
            <div className="flex justify-between text-xs text-parchment-muted mb-1 tabular">
              <span>{formatCurrency(progress.current_level_balance)}</span>
              <span>{formatCurrency(progress.next_level_balance)}</span>
            </div>
            <div className="h-3 rounded-full bg-surface-2 border border-glass overflow-hidden">
              <div
                className="h-full bg-gold-linear transition-all duration-500"
                style={{ width: `${progress.level_progress_pct}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-parchment-muted flex flex-wrap gap-x-2">
              <span>
                {t("dash.toLevel", {
                  pct: progress.level_progress_pct.toFixed(0),
                  level: progress.level + 1,
                })}
              </span>
              {/* A percentage does not tell you how much more to actually make. */}
              <span className="tabular text-accent">
                {t("dash.toGo", { amount: formatCurrency(progress.to_next_level) })}
              </span>
            </p>

            {progress.goal_amount != null && (
              <div className="mt-4">
                <div className="flex justify-between text-xs text-parchment-muted mb-1 tabular">
                  <span>{t("dash.goalProgress")}</span>
                  <span>
                    {t("dash.goalOf", {
                      pct: progress.goal_progress_pct?.toFixed(1) ?? "0",
                      amount: formatCurrency(progress.goal_amount),
                    })}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-surface-2 border border-glass overflow-hidden">
                  <div
                    className="h-full bg-emerald-400/70"
                    style={{ width: `${progress.goal_progress_pct ?? 0}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Quick stats */}
      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t("common.today")}
          value={formatSignedCurrency(stats.today_pl)}
          sub={tradesSub(stats.today_trades)}
          accent={pnlAccent(stats.today_pl)}
        />
        <StatCard
          label={t("dash.thisWeek")}
          value={formatSignedCurrency(stats.week_pl)}
          sub={tradesSub(stats.week_trades)}
          accent={pnlAccent(stats.week_pl)}
        />
        <StatCard
          label={t("dash.thisMonth")}
          value={formatSignedCurrency(stats.month_pl)}
          sub={tradesSub(stats.month_trades)}
          accent={pnlAccent(stats.month_pl)}
        />
        <StatCard
          label={t("dash.winRate")}
          value={`${stats.win_rate_pct.toFixed(1)}%`}
          sub={t("dash.totalCount", { count: stats.total_trades })}
          accent="text-accent"
        />
      </section>

      {/* Charts */}
      <section className="grid gap-4 xl:grid-cols-2">
        <ChartCard
          title={t("dash.accountGrowth")}
          Icon={LineChart}
          to="/wallet"
          loading={analyticsQ.isLoading}
          empty={!analyticsQ.data || analyticsQ.data.series.length === 0}
          emptyText={t("dash.emptyCashflow")}
        >
          {analyticsQ.data && <CashflowChart series={analyticsQ.data.series} />}
        </ChartCard>

        <ChartCard
          title={t("dash.dailyPnl")}
          Icon={BarChart3}
          to="/reports"
          loading={reportsQ.isLoading}
          empty={!reportsQ.data || reportsQ.data.by_day.length === 0}
          emptyText={t("dash.emptyClosedTrades")}
        >
          {reportsQ.data && <DailyPnlChart data={reportsQ.data.by_day} />}
        </ChartCard>
      </section>

      {/* This week, straight from the calendar */}
      <section className="glass p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-parchment flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-accent" />
            {t("dash.thisWeek")}
          </h2>
          <Link
            to="/calendar"
            className="text-xs font-semibold text-accent hover:text-accent inline-flex items-center gap-1"
          >
            {t("dash.seeAll")} <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
          {week.days.map((d) => (
            <WeekDayCell key={d.date} day={d} />
          ))}
        </div>
      </section>

      {/* Recent activity */}
      <section className="glass p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-parchment flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-accent" />
            {t("dash.recentActivity")}
          </h2>
          <Link to="/trades" className="text-xs font-semibold text-accent hover:text-accent inline-flex items-center gap-1">
            {t("dash.seeAll")} <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {activity.length === 0 ? (
          <p className="text-parchment-muted text-sm py-8 text-center">
            {t("dash.emptyActivity")}
          </p>
        ) : (
          <ul className="divide-y divide-glass">
            {activity.map((a, i) => (
              <ActivityRow key={i} item={a} />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function pnlAccent(v: number): string {
  return v > 0 ? "text-pnl-pos" : v < 0 ? "text-pnl-neg" : "text-parchment";
}

function SetupBanner() {
  const { t } = useI18n();
  return (
    <section className="glass p-5 border-gold-500/20 flex flex-wrap items-center gap-3 justify-between">
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-gold-500/15 border border-gold-500/30 text-accent">
          <Target className="w-4 h-4" />
        </span>
        <div>
          <p className="text-parchment font-semibold">{t("dash.setupTitle")}</p>
          <p className="text-xs text-parchment-muted">
            {t("dash.setupDesc")}
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <Link to="/wallet" className="btn-ghost">
          <Plus className="w-4 h-4" />
          {t("dash.addDeposit")}
        </Link>
        <Link to="/journey" className="btn-gold">
          <Target className="w-4 h-4" />
          {t("dash.setTarget")}
        </Link>
      </div>
    </section>
  );
}

function StatCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div className="glass p-4">
      <p className="label">{label}</p>
      <p className={`mt-2 text-2xl font-bold tabular ${accent ?? "text-parchment"}`}>{value}</p>
      {sub && <p className="text-xs text-parchment-muted mt-1">{sub}</p>}
    </div>
  );
}

function ChartCard({
  title,
  Icon,
  to,
  loading,
  empty,
  emptyText,
  children,
}: {
  title: string;
  Icon: React.ComponentType<{ className?: string }>;
  to: string;
  loading: boolean;
  empty: boolean;
  emptyText: string;
  children: React.ReactNode;
}) {
  const { t } = useI18n();
  return (
    <div className="glass p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-parchment flex items-center gap-2">
          <Icon className="w-5 h-5 text-accent" />
          {title}
        </h2>
        <Link
          to={to}
          className="text-xs font-semibold text-accent hover:text-accent inline-flex items-center gap-1"
        >
          {t("common.details")} <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      {loading ? (
        <div className="h-56 rounded-lg bg-surface-2/40 animate-pulse" />
      ) : empty ? (
        <p className="text-parchment-muted text-sm py-8 text-center">{emptyText}</p>
      ) : (
        children
      )}
    </div>
  );
}

/**
 * Diverging daily net-P&L bars (green up / red down) on a zero baseline, last
 * 30 trading days. Pure SVG (viewBox tracks real width so nothing stretches)
 * with a hover tooltip showing the exact figures.
 */
const DPL_HEIGHT = 220;
const DPL_PAD_X = 8;
const DPL_PAD_TOP = 14;
const DPL_PAD_BOTTOM = 22;

function DailyPnlChart({ data }: { data: DayBreakdown[] }) {
  const { t } = useI18n();
  const rows = data.slice(-30);
  const { ref, width } = useElementWidth<HTMLDivElement>(800);
  const [hover, setHover] = useState<number | null>(null);

  const vals = rows.map((r) => r.net_pl);
  const max = Math.max(0, ...vals);
  const min = Math.min(0, ...vals);
  const range = max - min || 1;
  const y = (v: number) =>
    DPL_PAD_TOP + (1 - (v - min) / range) * (DPL_HEIGHT - DPL_PAD_TOP - DPL_PAD_BOTTOM);
  const zeroY = y(0);

  const slot = (width - 2 * DPL_PAD_X) / (rows.length || 1);
  const barW = Math.max(2, Math.min(26, slot * 0.7));

  // Label roughly six evenly spaced days so the axis doesn't get crowded.
  const labelEvery = Math.max(1, Math.ceil(rows.length / 6));
  const shortDay = (d: string) => {
    const [, mo, day] = d.split("-");
    return `${Number(mo)}/${Number(day)}`;
  };

  const hv = hover != null ? rows[hover] : null;
  const hx = hover != null ? DPL_PAD_X + slot * hover + slot / 2 : 0;

  return (
    <div ref={ref} className="relative">
      <svg
        viewBox={`0 0 ${width} ${DPL_HEIGHT}`}
        width={width}
        height={DPL_HEIGHT}
        className="w-full"
        style={{ height: DPL_HEIGHT }}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          {/* Vertical gradients (light top → deeper bottom) give the bars a
              subtle glossy 3D lift. */}
          <linearGradient id="dplPos" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6ee7b7" />
            <stop offset="100%" stopColor="#059669" />
          </linearGradient>
          <linearGradient id="dplNeg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fda4af" />
            <stop offset="100%" stopColor="#e11d48" />
          </linearGradient>
        </defs>
        <line
          x1={DPL_PAD_X}
          x2={width - DPL_PAD_X}
          y1={zeroY}
          y2={zeroY}
          stroke="rgb(var(--text) / 0.15)"
          strokeDasharray="4 4"
        />
        {rows.map((r, i) => {
          const cx = DPL_PAD_X + slot * i + slot / 2;
          const top = y(Math.max(r.net_pl, 0));
          const bottom = y(Math.min(r.net_pl, 0));
          const h = Math.max(1, bottom - top);
          const pos = r.net_pl >= 0;
          return (
            <g key={r.date} onMouseEnter={() => setHover(i)}>
              {/* full-height hit target so hover works anywhere in the column */}
              <rect
                x={DPL_PAD_X + slot * i}
                y={DPL_PAD_TOP}
                width={slot}
                height={DPL_HEIGHT - DPL_PAD_TOP - DPL_PAD_BOTTOM}
                fill="transparent"
              />
              <rect
                x={cx - barW / 2}
                y={top}
                width={barW}
                height={h}
                rx={Math.min(4, barW / 2)}
                fill={pos ? "url(#dplPos)" : "url(#dplNeg)"}
                fillOpacity={hover == null || hover === i ? 1 : 0.4}
              />
              {/* thin top highlight for the glossy edge */}
              <rect
                x={cx - barW / 2}
                y={top}
                width={barW}
                height={Math.min(3, h)}
                rx={Math.min(4, barW / 2)}
                fill="#ffffff"
                fillOpacity={hover == null || hover === i ? 0.28 : 0.12}
              />
              {i % labelEvery === 0 && (
                <text
                  x={cx}
                  y={DPL_HEIGHT - 6}
                  textAnchor="middle"
                  className="fill-parchment-muted text-[10px]"
                >
                  {shortDay(r.date)}
                </text>
              )}
            </g>
          );
        })}
        <text x={DPL_PAD_X} y={12} className="fill-parchment-muted text-[10px]">
          {formatSignedCurrency(max)}
        </text>
        {min < 0 && (
          <text x={DPL_PAD_X} y={DPL_HEIGHT - DPL_PAD_BOTTOM - 2} className="fill-parchment-muted text-[10px]">
            {formatSignedCurrency(min)}
          </text>
        )}
      </svg>

      {hv && (
        <div
          className="pointer-events-none absolute top-0 z-10 rounded-lg border border-glass px-3 py-2 text-xs shadow-lg"
          style={{
            insetInlineStart: `${Math.min(Math.max((hx / width) * 100, 12), 88)}%`,
            transform: "translateX(-50%)",
            backgroundColor: "rgb(var(--bg-950) / 0.62)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
          }}
        >
          <p className="text-parchment-muted tabular mb-1">
            {new Date(hv.date + "T00:00:00").toLocaleDateString()}
          </p>
          <p className="flex items-center gap-3 text-parchment">
            {t("dash.netPnl")}
            <span
              className={
                "tabular font-semibold ms-auto " +
                (hv.net_pl > 0 ? "text-pnl-pos" : hv.net_pl < 0 ? "text-pnl-neg" : "text-parchment")
              }
            >
              {formatSignedCurrency(hv.net_pl)}
            </span>
          </p>
          <p className="flex items-center gap-3 text-parchment-muted">
            {t("nav.trades")}
            <span className="tabular font-semibold ms-auto">{hv.trades}</span>
          </p>
        </div>
      )}
    </div>
  );
}

function ActivityRow({ item }: { item: DashboardSummary["activity"][number] }) {
  const { t: tr } = useI18n();
  if (item.kind === "wallet") {
    const w = item.wallet;
    const isDep = w.type === "deposit";
    // A banked withdrawal is money set aside, not money lost -- gold, not red.
    const isSave = !isDep && w.is_saving;
    const Icon = isDep ? ArrowDownToLine : ArrowUpFromLine;
    return (
      <li className="py-2.5 flex items-center gap-3">
        <span
          className={
            "inline-flex items-center justify-center w-7 h-7 rounded-lg border " +
            (isDep
              ? "bg-sky-500/10 border-sky-500/25 text-sky-400"
              : isSave
                ? "bg-gold-500/15 border-gold-500/25 text-accent"
                : "bg-orange-500/10 border-orange-500/25 text-orange-400")
          }
        >
          <Icon className="w-3.5 h-3.5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-parchment text-sm">
            <span className="font-semibold">{isDep ? tr("dash.deposit") : tr("dash.withdrawal")}</span>
            {w.is_saving && <span className="chip-gold text-[10px] px-2 py-0.5">{tr("dash.savingChip")}</span>}
          </div>
          <p className="text-xs text-parchment-muted">{formatDate(item.ts)}</p>
        </div>
        <div
          className={
            "text-end tabular font-semibold " +
            (isDep ? "text-sky-400" : isSave ? "text-accent" : "text-orange-400")
          }
        >
          {formatSignedCurrency(isDep ? w.amount : -w.amount)}
        </div>
      </li>
    );
  }

  const t = item.trade;
  const isOpen = t.status === "open";
  const cls = isOpen
    ? "text-amber-300"
    : t.net_pl > 0
      ? "text-pnl-pos"
      : t.net_pl < 0
        ? "text-pnl-neg"
        : "text-parchment";
  return (
    <li className="py-2.5 flex items-center gap-3">
      <span
        className={
          "inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider " +
          (t.side === "Buy" ? "bg-emerald-500/15 text-pnl-pos" : "bg-rose-500/15 text-pnl-neg")
        }
      >
        {t.side}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono font-semibold text-parchment">{t.symbol || "—"}</span>
          {isOpen && <span className="chip-gold text-[10px] px-2 py-0.5">{tr("dash.open")}</span>}
        </div>
        <p className="text-xs text-parchment-muted">{formatDate(item.ts)}</p>
      </div>
      <div className={`text-end tabular font-semibold ${cls}`}>
        {isOpen ? "—" : formatSignedCurrency(t.net_pl)}
      </div>
    </li>
  );
}
